import PropTypes from 'prop-types';
import { useContext, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

// mantine
import { Badge, Indicator, NavLink, VisuallyHidden } from '@mantine/core';
import { IconAlertTriangle, IconSparkles } from '@tabler/icons-react';

// project imports
import DrawerContext from 'contexts/DrawerContext';
import { handleLogout } from 'utils/auth-helpers';
import OTPDialog from 'views/public/signup/dialogs/OTPDialog';
import { radius, semanticColors } from 'theme';

const customizationConfig = {
  basename: '',
  defaultPath: '/',
  fontFamily: `'Roboto', sans-serif`,
  borderRadius: 12
};

/**
 * INBOX_NAV_COPY's badge strings (host-inbox-tech-design §4.6). Nothing here
 * is inbox-specific: any item can carry a count.
 */
const BADGE_COPY = {
  badgeSr: (n) => `${n} new`,
  badgeMax: '99+',
  // Joins the hidden count to the link's name, so it reads "Inbox, 3 new".
  srJoin: ', '
};

const BADGE_MAX = 99;

/**
 * The count's fill, keyed by whether the item is the current page (§4.7).
 * White on coral[6] is 3.2:1, too low for a 12 px count, so an idle badge sits
 * on coralTextStrong: coral[8] with white text in light, coral[3] with dark ink
 * in dark (text.inverse is that pair). The active item is itself a coral fill,
 * so there the badge inverts to the page surface with coralTextStrong text,
 * the pair that token is tuned for: 5.6:1 in light, 8.3:1 in dark.
 */
const BADGE_TONE_BY_ACTIVE = {
  false: { backgroundColor: semanticColors.brand.coralTextStrong, color: semanticColors.text.inverse },
  true: { backgroundColor: semanticColors.surface.base, color: semanticColors.brand.coralTextStrong }
};

/**
 * The label and icon color. The active item inherits `--nl-color`, the label
 * color Mantine's filled variant pairs with its coral fill in each scheme.
 */
const ITEM_COLOR_BY_ACTIVE = {
  false: semanticColors.text.primary,
  true: 'inherit'
};

/**
 * The opt-in `pill` (host-availability-ui-design §3.11): a word, not a count,
 * such as Calendar's "Set up" while the host's time zone is unconfirmed. A
 * sand pill with a sparkle; ink on the sand surface in both schemes, so it
 * reads on the active item's coral fill too. Collapsed, it is a sand dot.
 */
const PILL_STYLES = {
  root: {
    background: semanticColors.brand.sandSurface,
    border: `1px solid ${semanticColors.brand.sandSurfaceBorder}`,
    color: 'light-dark(var(--mantine-color-sand-8), var(--dt-brand-sandAccentText))',
    letterSpacing: 0,
    fontWeight: 600
  }
};

/** A whole, positive count, or 0 for anything else (a missing badge included). */
const toBadgeCount = (value) => (Number.isFinite(value) && value > 0 ? Math.floor(value) : 0);

const badgeText = (count) => (count > BADGE_MAX ? BADGE_COPY.badgeMax : String(count));

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level }) => {
  const { pathname } = useLocation();

  const { leftDrawerOpened: opened } = useContext(DrawerContext);
  const [openOTPAlertDialog, setOpenOTPAlertDialog] = useState(false);

  // if there are multiple pathname then get only the first one
  const mainPath = pathname.split('/').length > 2 ? `/${pathname.split('/')[1]}` : pathname;
  // `exact` items match their own URL only: `/host` (My Services) would
  // otherwise also light up on /host/packages and /host/profile, whose first
  // segment is the same. `nested` items also match anything under their URL,
  // which the first-segment rule cannot see for a two-segment URL like
  // /host/inbox.
  const isUnderItem = Boolean(item.nested) && pathname.startsWith(`${item.url}/`);
  const isNavItemSelected = item.exact ? pathname === item.url : mainPath === item.url || pathname === item.url || isUnderItem;

  const itemColor = ITEM_COLOR_BY_ACTIVE[String(isNavItemSelected)];
  const Icon = item.disabled ? IconAlertTriangle : item.icon;
  // currentColor: the icon takes the section's color below.
  const icon = Icon ? <Icon size={20} stroke={1.5} /> : null;

  /*
   * The opt-in `badge` (host-inbox-tech-design §4.5.11): hidden at 0, "99+"
   * above 99. Expanded, the count is a Badge in the right section; collapsed to
   * icons, it is a dot on the icon. Either way the link carries the count as
   * hidden text, and collapsed, where there is no visible label, the title too.
   * An item without a badge renders exactly as before.
   */
  const badgeCount = toBadgeCount(item.badge);
  const badgeTone = BADGE_TONE_BY_ACTIVE[String(isNavItemSelected)];
  const badgeSrOf = item.badgeSr ?? BADGE_COPY.badgeSr;
  const badgeSr = `${opened ? '' : item.title}${BADGE_COPY.srJoin}${badgeSrOf(badgeCount)}`;

  const { pill } = item;
  const pillSr = pill ? `${opened ? '' : item.title}${BADGE_COPY.srJoin}${pill.sr}` : '';

  let leftSection = icon;
  let rightSection;
  if (badgeCount === 0 && pill && opened) {
    rightSection = (
      <>
        <Badge
          component="span"
          aria-hidden
          size="sm"
          radius="pill"
          tt="none"
          leftSection={<IconSparkles size={12} stroke={1.8} />}
          styles={PILL_STYLES}
        >
          {pill.label}
        </Badge>
        <VisuallyHidden>{pillSr}</VisuallyHidden>
      </>
    );
  } else if (badgeCount === 0 && pill) {
    leftSection = (
      <>
        <Indicator color={semanticColors.brand.sandAccentText} size={9} offset={2} withBorder>
          {icon}
        </Indicator>
        <VisuallyHidden>{pillSr}</VisuallyHidden>
      </>
    );
  } else if (badgeCount > 0 && opened) {
    rightSection = (
      <>
        <Badge component="span" aria-hidden size="md" radius="pill" miw={22} px={7} styles={{ root: badgeTone }}>
          {badgeText(badgeCount)}
        </Badge>
        <VisuallyHidden>{badgeSr}</VisuallyHidden>
      </>
    );
  } else if (badgeCount > 0) {
    leftSection = (
      <>
        <Indicator color={badgeTone.backgroundColor} size={9} offset={2} withBorder>
          {icon}
        </Indicator>
        <VisuallyHidden>{badgeSr}</VisuallyHidden>
      </>
    );
  }

  // Mobile nav close is handled by MainLayout's route-change effect —
  // setting leftDrawerOpened here would corrupt the DESKTOP collapse state.
  const itemHandler = async (id) => {
    if (id === 'logout') {
      await handleLogout();
      window.location.assign('/login');
    }
  };

  const handleClick = (e) => {
    if (item.disabled) {
      e.preventDefault();
      setOpenOTPAlertDialog(true);
      return;
    }
    itemHandler(item.id);
  };

  return (
    <>
      <NavLink
        component={Link}
        to={item.url ? `${customizationConfig.basename}${item.url}` : '#'}
        target={item.target ? '_blank' : '_self'}
        label={opened ? item.title : ''}
        description={item.caption && opened ? item.caption : undefined}
        leftSection={leftSection}
        rightSection={rightSection}
        active={isNavItemSelected}
        onClick={handleClick}
        style={{
          borderRadius: radius.control,
          marginBottom: 4,
          paddingLeft: level * 24
        }}
        styles={{
          label: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: isNavItemSelected ? 600 : 400,
            color: itemColor
          },
          section: {
            color: itemColor,
            minWidth: 36
          }
        }}
        variant={isNavItemSelected ? 'filled' : 'subtle'}
        color={isNavItemSelected ? 'coral' : 'gray'}
      />
      <OTPDialog open={openOTPAlertDialog} setOpen={setOpenOTPAlertDialog} navigateOnClose="/my-apps" />
    </>
  );
};

NavItem.propTypes = {
  /**
   * A menu entry: `{ id, title, url, icon, … }`, plus the opt-ins `exact`,
   * `nested` (see above), `badge`, a count shown beside the title,
   * `badgeSr`, `(n) => string`, its hidden wording when "{n} new" is wrong,
   * and `pill`, `{ label, sr }`, a word beside the title ("Set up") with its
   * hidden wording ("set up needed"), shown when there is no count.
   */
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavItem;
